import React, { useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { DesignElement as DesignElementType, CouponDesign, Breakpoint, GridSettings } from '../../../types/coupon';
import DesignElement from './DesignElement';

interface DesignerCanvasProps {
    design: CouponDesign;
    onDesignChange: (design: CouponDesign) => void;
    currentBreakpoint: Breakpoint;
    selectedElementId: string | null;
    onElementSelect: (elementId: string | null) => void;
    onElementUpdate: (element: DesignElementType) => void;
    onElementsUpdate: (elements: DesignElementType[]) => void;
    gridSettings: GridSettings;
    couponId?: string;
}

export const DesignerCanvas: React.FC<DesignerCanvasProps> = ({
    design,
    onDesignChange,
    currentBreakpoint,
    selectedElementId,
    onElementSelect,
    onElementUpdate,
    onElementsUpdate,
    gridSettings,
    couponId
}) => {
    const canvasRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    // Get base URL for images from environment or default
    const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

    const getImageUrl = (path: string) => {
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        return process.env.NODE_ENV === 'development' ? `${url}?t=${new Date().getTime()}` : url;
    };

    return (
        <Box
            ref={canvasRef}
            onClick={() => onElementSelect(null)}
            sx={{
                position: 'relative',
                width: design.canvas.breakpoints[currentBreakpoint].width,
                height: design.canvas.breakpoints[currentBreakpoint].height,
                backgroundColor: design.canvas.backgroundColor || '#ffffff',
                border: '1px solid #ccc',
                overflow: 'hidden'
            }}
        >
            {/* Background Image Layer */}
            {design.canvas.background && (
                <Box
                    component="img"
                    src={getImageUrl(design.canvas.background.url)}
                    alt={design.canvas.background.originalName || 'Canvas Background'}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: design.canvas.background.fit,
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
            )}

            {/* Grid Layer */}
            {gridSettings.show && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `linear-gradient(to right, ${theme.palette.grey[200]} 1px, transparent 1px),
                                       linear-gradient(to bottom, ${theme.palette.grey[200]} 1px, transparent 1px)`,
                        backgroundSize: `${gridSettings.size}px ${gridSettings.size}px`,
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
            )}

            {design.elements.map(element => (
                <DesignElement
                    key={element.id}
                    element={element}
                    breakpoint={currentBreakpoint}
                    selected={element.id === selectedElementId}
                    onClick={(e) => {
                        e.stopPropagation();
                        onElementSelect(element.id);
                    }}
                    onUpdate={onElementUpdate}
                    gridSettings={gridSettings}
                />
            ))}
        </Box>
    );
};
